import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tabs,
  Button,
  Space,
  Input,
  Select,
  Table,
  Tag,
  Avatar,
  Modal,
  Form,
  message,
  Statistic,
  Tooltip,
  Badge,
  Dropdown,
  Popconfirm
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PhoneOutlined,
  MailOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  TeamOutlined,
  StarOutlined,
  MoreOutlined,
  FilterOutlined,
  ExportOutlined,
  ImportOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import CustomerProfile from '../components/CustomerProfile';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  industry: string;
  location: string;
  status: 'active' | 'inactive' | 'prospect' | 'lead';
  tier: 'platinum' | 'gold' | 'silver' | 'bronze';
  totalRevenue: number;
  totalOrders: number;
  avgOrderValue: number;
  lastActivity: string;
  lastPurchase: string;
  createdAt: string;
  tags: string[];
  salesRep: string;
  satisfaction: number;
  riskScore: number;
}

const CustomerManagementPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterTier, setFilterTier] = useState<string>('all');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerModalVisible, setCustomerModalVisible] = useState(false);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      // Real API call to fetch customers
      const response = await fetch('http://localhost:3001/api/customers?page=1&limit=100');
      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }
      
      const data = await response.json();
      
      // Transform API data to match our interface
      const transformedCustomers: Customer[] = data.customers.map((customer: any) => ({
        id: customer.id,
        name: customer.name,
        email: customer.email || '',
        phone: customer.phone || '',
        company: customer.company?.name || 'Unknown Company',
        industry: 'Technology', // Default since not in API
        location: customer.address || 'Unknown Location',
        status: customer.isActive ? 'active' : 'inactive',
        tier: 'silver', // Default since not in API
        totalRevenue: Math.random() * 100000, // Mock for now
        totalOrders: customer._count?.salesOrders || 0,
        avgOrderValue: Math.random() * 5000, // Mock for now
        lastActivity: new Date(customer.updatedAt).toISOString().split('T')[0],
        lastPurchase: customer._count?.salesOrders > 0 ? new Date(customer.updatedAt).toISOString().split('T')[0] : '',
        createdAt: new Date(customer.createdAt).toISOString().split('T')[0],
        tags: [], // Default since not in API
        salesRep: 'Sales Team', // Default since not in API
        satisfaction: Math.floor(Math.random() * 30) + 70, // Mock 70-100%
        riskScore: Math.floor(Math.random() * 50) + 10 // Mock 10-60
      }));

      setCustomers(transformedCustomers);
      message.success(`Loaded ${transformedCustomers.length} customers from database`);
    } catch (error) {
      console.error('Error fetching customers:', error);
      message.error('Failed to fetch customers from database, using mock data');
      
      // Fallback to mock data if API fails
      const mockCustomers: Customer[] = [
        {
          id: '1',
          name: 'John Smith',
          email: 'john.smith@acme.com',
          phone: '+1-555-0123',
          company: 'Acme Corporation',
          industry: 'Technology',
          location: 'New York, NY',
          status: 'active',
          tier: 'platinum',
          totalRevenue: 125000,
          totalOrders: 45,
          avgOrderValue: 2777.78,
          lastActivity: '2024-06-23',
          lastPurchase: '2024-06-20',
          createdAt: '2023-01-15',
          tags: ['VIP', 'Enterprise', 'Long-term'],
          salesRep: 'Sarah Johnson',
          satisfaction: 95,
          riskScore: 15
        },
        {
          id: '2',
          name: 'Emily Davis',
          email: 'emily.davis@globaltech.com',
          phone: '+1-555-0456',
          company: 'Global Tech Solutions',
          industry: 'Software',
          location: 'San Francisco, CA',
          status: 'active',
          tier: 'gold',
          totalRevenue: 85000,
          totalOrders: 32,
          avgOrderValue: 2656.25,
          lastActivity: '2024-06-22',
          lastPurchase: '2024-06-18',
          createdAt: '2023-03-20',
          tags: ['Tech-savvy', 'Quick payments'],
          salesRep: 'Mike Chen',
          satisfaction: 88,
          riskScore: 25
        },
        {
          id: '3',
          name: 'Robert Wilson',
          email: 'robert.wilson@startup.io',
          phone: '+1-555-0789',
          company: 'Innovative Startup',
          industry: 'Fintech',
          location: 'Austin, TX',
          status: 'prospect',
          tier: 'silver',
          totalRevenue: 15000,
          totalOrders: 8,
          avgOrderValue: 1875,
          lastActivity: '2024-06-24',
          lastPurchase: '2024-05-15',
          createdAt: '2024-02-10',
          tags: ['Startup', 'Growth potential'],
          salesRep: 'Lisa Wang',
          satisfaction: 82,
          riskScore: 40
        },
        {
          id: '4',
          name: 'Sarah Miller',
          email: 'sarah@enterprise.com',
          phone: '+1-555-0987',
          company: 'Enterprise Corp',
          industry: 'Manufacturing',
          location: 'Chicago, IL',
          status: 'active',
          tier: 'gold',
          totalRevenue: 95000,
          totalOrders: 28,
          avgOrderValue: 3392.86,
          lastActivity: '2024-06-21',
          lastPurchase: '2024-06-19',
          createdAt: '2023-05-10',
          tags: ['Reliable', 'Bulk orders'],
          salesRep: 'David Brown',
          satisfaction: 92,
          riskScore: 20
        },
        {
          id: '5',
          name: 'Michael Chen',
          email: 'mike@globaltech.com',
          phone: '+1-555-0654',
          company: 'Global Tech Solutions',
          industry: 'Technology',
          location: 'Seattle, WA',
          status: 'inactive',
          tier: 'bronze',
          totalRevenue: 12000,
          totalOrders: 5,
          avgOrderValue: 2400,
          lastActivity: '2024-05-10',
          lastPurchase: '2024-04-20',
          createdAt: '2024-01-05',
          tags: ['New customer', 'Potential'],
          salesRep: 'Alex Thompson',
          satisfaction: 75,
          riskScore: 55
        }
      ];

      setCustomers(mockCustomers);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'green',
      inactive: 'red',
      prospect: 'blue',
      lead: 'orange'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getTierColor = (tier: string) => {
    const colors = {
      platinum: 'purple',
      gold: 'gold',
      silver: 'default',
      bronze: 'brown'
    };
    return colors[tier as keyof typeof colors] || 'default';
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.company.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || customer.status === filterStatus;
    const matchesTier = filterTier === 'all' || customer.tier === filterTier;
    
    return matchesSearch && matchesStatus && matchesTier;
  });

  const handleAddCustomer = () => {
    form.resetFields();
    setSelectedCustomer(null);
    setCustomerModalVisible(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    form.setFieldsValue(customer);
    setCustomerModalVisible(true);
  };

  const handleViewCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setProfileModalVisible(true);
  };

  const handleDeleteCustomer = (customerId: string) => {
    setCustomers(customers.filter(c => c.id !== customerId));
    message.success('Customer deleted successfully');
  };

  const handleSaveCustomer = async (values: any) => {
    try {
      if (selectedCustomer) {
        // Update existing customer
        setCustomers(customers.map(c => 
          c.id === selectedCustomer.id ? { ...c, ...values } : c
        ));
        message.success('Customer updated successfully');
      } else {
        // Add new customer
        const newCustomer: Customer = {
          id: Date.now().toString(),
          ...values,
          totalRevenue: 0,
          totalOrders: 0,
          avgOrderValue: 0,
          lastActivity: new Date().toISOString().split('T')[0],
          lastPurchase: '',
          createdAt: new Date().toISOString().split('T')[0],
          tags: [],
          satisfaction: 0,
          riskScore: 0
        };
        setCustomers([...customers, newCustomer]);
        message.success('Customer added successfully');
      }
      setCustomerModalVisible(false);
    } catch (error) {
      message.error('Failed to save customer');
    }
  };

  const customerColumns: ColumnsType<Customer> = [
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>{record.company}</div>
          </div>
        </Space>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div><MailOutlined /> {record.email}</div>
          <div><PhoneOutlined /> {record.phone}</div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      render: (tier) => (
        <Tag color={getTierColor(tier)}>
          {tier.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (revenue) => `$${revenue.toLocaleString()}`,
      sorter: (a, b) => a.totalRevenue - b.totalRevenue
    },
    {
      title: 'Orders',
      dataIndex: 'totalOrders',
      key: 'totalOrders',
      sorter: (a, b) => a.totalOrders - b.totalOrders
    },
    {
      title: 'Satisfaction',
      dataIndex: 'satisfaction',
      key: 'satisfaction',
      render: (satisfaction) => (
        <Space>
          <StarOutlined style={{ color: '#faad14' }} />
          {satisfaction}%
        </Space>
      ),
      sorter: (a, b) => a.satisfaction - b.satisfaction
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Profile">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewCustomer(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEditCustomer(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this customer?"
            onConfirm={() => handleDeleteCustomer(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button 
                type="text" 
                icon={<DeleteOutlined />} 
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const stats = {
    totalCustomers: customers.length,
    activeCustomers: customers.filter(c => c.status === 'active').length,
    totalRevenue: customers.reduce((sum, c) => sum + c.totalRevenue, 0),
    avgSatisfaction: customers.reduce((sum, c) => sum + c.satisfaction, 0) / customers.length || 0
  };

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <UserOutlined style={{ marginRight: 8 }} />
            Customer Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCustomer}>
              Add Customer
            </Button>
            <Button icon={<ImportOutlined />}>
              Import
            </Button>
            <Button icon={<ExportOutlined />}>
              Export
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Customers"
              value={stats.totalCustomers}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Customers"
              value={stats.activeCustomers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={stats.totalRevenue}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Satisfaction"
              value={stats.avgSatisfaction}
              suffix="%"
              prefix={<StarOutlined />}
              precision={1}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Search
              placeholder="Search customers by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', maxWidth: 400 }}
            />
          </Col>
          <Col>
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 120 }}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="prospect">Prospect</Option>
              <Option value="lead">Lead</Option>
            </Select>
          </Col>
          <Col>
            <Select
              value={filterTier}
              onChange={setFilterTier}
              style={{ width: 120 }}
            >
              <Option value="all">All Tiers</Option>
              <Option value="platinum">Platinum</Option>
              <Option value="gold">Gold</Option>
              <Option value="silver">Silver</Option>
              <Option value="bronze">Bronze</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Customer Table */}
      <Card>
        <Table
          columns={customerColumns}
          dataSource={filteredCustomers}
          rowKey="id"
          loading={loading}
          pagination={{
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} customers`
          }}
        />
      </Card>

      {/* Add/Edit Customer Modal */}
      <Modal
        title={selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
        open={customerModalVisible}
        onCancel={() => setCustomerModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveCustomer}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Customer Name"
                name="name"
                rules={[{ required: true, message: 'Please enter customer name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Company"
                name="company"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Phone"
                name="phone"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Industry"
                name="industry"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Location"
                name="location"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="prospect">Prospect</Option>
                  <Option value="lead">Lead</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Tier"
                name="tier"
                rules={[{ required: true, message: 'Please select tier' }]}
              >
                <Select>
                  <Option value="platinum">Platinum</Option>
                  <Option value="gold">Gold</Option>
                  <Option value="silver">Silver</Option>
                  <Option value="bronze">Bronze</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Sales Representative"
            name="salesRep"
          >
            <Input />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCustomerModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {selectedCustomer ? 'Update' : 'Add'} Customer
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Customer Profile Modal */}
      <Modal
        title="Customer Profile"
        open={profileModalVisible}
        onCancel={() => setProfileModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedCustomer && (
          <CustomerProfile 
            customerId={selectedCustomer.id}
            onClose={() => setProfileModalVisible(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default CustomerManagementPage; 